// Câmera da frota (pedido 2026-09-16) — menu "Câmera" do app da empresa,
// visível só quando o Super Master ativa o módulo (ver app-sidebar.tsx) e
// quem está logado não é cliente final (accountType "usuario"). Sessão
// comum do GPS Drive (gpsDriveGet/getGpsDriveAccessToken), nunca a sessão
// de plataforma (essa é jt808-camera.service.ts, ferramenta de diagnóstico
// do Super Master para um único IMEI de teste).
import {
  GPSDRIVE_API_URL,
  getGpsDriveAccessToken,
  gpsDriveDelete,
  gpsDriveFetchBlob,
  gpsDriveGet,
  gpsDrivePostBinary,
  gpsDriveUpload,
} from "@/lib/api/gpsdrive-client";

// Motorista vinculado ao veículo na Gestão de Frota + facial cadastrada pra
// reconhecimento pela câmera interna (2026-10-22).
export interface FleetCameraDriver {
  id: string;
  name: string;
  phone: string | null;
  photoUrl: string | null;
  faceUrl: string | null;
  faceUpdatedAt: string | null;
}

export interface FleetCameraIdentifyResult {
  driver: FleetCameraDriver;
  facePresent: boolean;
  samePerson: boolean | null;
  confidence: number;
  reason: string;
  checkedAt: string;
}

export interface FleetCameraVehicle {
  deviceId: number;
  name: string | null;
}

export interface FleetCameraPhoto {
  file: string;
  sizeBytes: number;
  modifiedAt: string;
  url: string;
}

// Alertas da câmera (2026-09-28) — celular, fadiga, cigarro, distração,
// ADAS… + saúde (cartão SD, sinal de vídeo). Ver src/jt808/jt808-alarms.ts
// no servidor JT808.
export interface FleetCameraAlarmEvent {
  id: string;
  type: string;
  label: string;
  severity: "critical" | "warning" | "info";
  source: string;
  level?: number;
  speedKmh: number;
  latitude: number;
  longitude: number;
  receivedAt: string;
}

export interface FleetCameraAlarms {
  events: FleetCameraAlarmEvent[];
  health: {
    storageFault: boolean;
    videoSignalLoss: boolean;
    videoOcclusion: boolean;
    otherDeviceFault: boolean;
    updatedAt: string;
  } | null;
}

export const fleetCameraService = {
  listAlarms: (deviceId: number) => gpsDriveGet<FleetCameraAlarms>(`/api/v1/fleet-camera/${deviceId}/alarms`),
  // Falar com o motorista (2026-09-30): PCM 16-bit 8 kHz mono cru → toca no
  // alto-falante da câmera. Só responde depois de enviar o áudio inteiro.
  sendTalk: (deviceId: number, pcm: ArrayBuffer) =>
    gpsDrivePostBinary<{ ok: boolean; message: string; seconds?: number }>(`/api/v1/fleet-camera/${deviceId}/talk`, pcm),
  getDriver: (deviceId: number) =>
    gpsDriveGet<{ driver: FleetCameraDriver | null }>(`/api/v1/fleet-camera/${deviceId}/driver`),
  uploadFace: (deviceId: number, image: File) =>
    gpsDriveUpload<{ driver: FleetCameraDriver }>(`/api/v1/fleet-camera/${deviceId}/driver/face`, image),
  deleteFace: (deviceId: number) =>
    gpsDriveDelete<{ driver: FleetCameraDriver }>(`/api/v1/fleet-camera/${deviceId}/driver/face`),
  identify: (deviceId: number, frame: File) =>
    gpsDriveUpload<FleetCameraIdentifyResult>(`/api/v1/fleet-camera/${deviceId}/driver/identify`, frame),
  listVehicles: () => gpsDriveGet<FleetCameraVehicle[]>("/api/v1/fleet-camera/vehicles"),
  listPhotos: (deviceId: number) => gpsDriveGet<FleetCameraPhoto[]>(`/api/v1/fleet-camera/${deviceId}/photos`),
  fetchPhotoBlob: (photo: FleetCameraPhoto) => gpsDriveFetchBlob(photo.url),
  // URL absoluta (mpegts.js faz sua própria requisição HTTP) — token vai
  // via header custom, nunca query string, mesmo padrão de
  // jt808CameraService#getLiveVideoRequest.
  // audio=true → vídeo com o som da cabine (motorista falando).
  getLiveVideoRequest: (deviceId: number, channel: 1 | 2, audio = false): { url: string; headers: Record<string, string> } => ({
    url: `${GPSDRIVE_API_URL}/api/v1/fleet-camera/${deviceId}/live.flv?channel=${channel}${audio ? "&audio=1" : ""}`,
    headers: getGpsDriveAccessToken() ? { Authorization: `Bearer ${getGpsDriveAccessToken()}` } : {},
  }),
};
